import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';

import {
    Button,
    TextField,
    Container,
    Typography,
    Box,
    Alert,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Paper,
    IconButton,
    InputAdornment,
    CircularProgress,
    useTheme,
    Snackbar
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { cores } from '../../theme';
import logoMedicina from '../../assets/logo-medicina.png';

const CadastroPagina = () => {
    const [nome, setNome] = useState('');
    const [matricula, setMatricula] = useState('');
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [confirmarSenha, setConfirmarSenha] = useState('');
    const [erro, setErro] = useState('');
    const [cargo, setCargo] = useState('ALUNO');
    const [carregando, setCarregando] = useState(false);
    const [mostrarSenha, setMostrarSenha] = useState(false);
    const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);
    const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });
    const theme = useTheme();

    const navegar = useNavigate();

    useEffect(() => {
        const originalBodyStyle = document.body.style.cssText;
        const originalHtmlStyle = document.documentElement.style.cssText;

        document.body.style.margin = '0';
        document.body.style.padding = '0';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.margin = '0';
        document.documentElement.style.padding = '0';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.cssText = originalBodyStyle;
            document.documentElement.style.cssText = originalHtmlStyle;
        };
    }, []);

    const handleCloseFeedback = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setFeedback({ ...feedback, open: false });

        if (feedback.severity === 'success') {
            navegar('/');
        }
    };

    const lidarComSubmissao = async (e) => {
        e.preventDefault();
        setErro('');
        setCarregando(true);

        if (senha !== confirmarSenha) {
            setCarregando(false);
            return setErro('As senhas não conferem.');
        }

        try {
            const dadosParaAtivacao = {
                email: email,
                senha: senha,
                nome_completo: nome,
                matricula: cargo === 'ALUNO' ? matricula : null,
                cargo: cargo,
            };

            const url_api = `${process.env.REACT_APP_API_BASE_URL}/api/usuarios/ativar-conta`;
            await axios.post(url_api, dadosParaAtivacao);

            setFeedback({
                open: true,
                message: 'Conta ativada com sucesso!',
                severity: 'success'
            });

            setTimeout(() => {
                navegar('/');
            }, 2000);

        } catch (err) {
            if (err.response) {
                setErro(err.response.data.erro || 'Ocorreu um erro ao ativar a conta.');
            } else {
                setErro('Falha de conexão. Tente novamente.');
            }
        } finally {
            setCarregando(false);
        }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                minHeight: '100vh',
                height: '100vh',
                width: '100vw',
                background: cores.gradientes.primario,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                overflow: { xs: 'auto', md: 'hidden' },
                py: { xs: 1, md: 0 },
                // Esconder scrollbar mas manter funcionalidade
                '&::-webkit-scrollbar': {
                    display: 'none',
                },
                msOverflowStyle: 'none', // IE e Edge
                scrollbarWidth: 'none', // Firefox
            }}
        >
            <Snackbar
                open={feedback.open}
                autoHideDuration={4000}
                onClose={handleCloseFeedback}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseFeedback}
                    severity={feedback.severity}
                    variant="filled"
                    sx={{
                        width: '100%',
                        boxShadow: cores.sombras.medio,
                        backgroundColor: feedback.severity === 'success' ? cores.sucesso.principal : cores.erro.principal
                    }}
                >
                    {feedback.message}
                </Alert>
            </Snackbar>

            <Container maxWidth="xs" disableGutters sx={{ px: 1, maxWidth: { xs: '95%', sm: '450px' } }}>
                <Paper
                    elevation={8}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        borderRadius: 3,
                        background: cores.fundo.papel,
                        boxShadow: cores.sombras.elevada,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxWidth: '100%',
                        width: '100%',
                        boxSizing: 'border-box',
                        my: { xs: 1, md: 0 },
                        maxHeight: { xs: 'none', md: '90vh' },
                        overflowY: { xs: 'visible', md: 'auto' },
                        // Esconder scrollbar do Paper também
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                        msOverflowStyle: 'none', // IE e Edge
                        scrollbarWidth: 'none', // Firefox
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: 80, sm: 100 },
                            height: { xs: 80, sm: 100 },
                            background: 'white',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: cores.sombras.padrao,
                            mb: 2,
                            p: 1,
                        }}
                    >
                        <img
                            src={logoMedicina}
                            alt="Logo Medicina"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                    <Typography component="h1" variant="h5" sx={{ fontWeight: 700, color: cores.primaria.principal, mb: 1, textAlign: 'center' }}>
                        RealMed
                    </Typography>
                    <Typography variant="body2" sx={{ color: cores.texto.secundario, mb: 3, textAlign: 'center' }}>
                        Crie sua conta para continuar
                    </Typography>
                    <Box component="form" onSubmit={lidarComSubmissao} sx={{ width: '100%' }}>
                        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

                        <FormControl component="fieldset" margin="normal" fullWidth>
                            <FormLabel
                                component="legend"
                                sx={{
                                    color: cores.primaria.principal,
                                    fontWeight: 600,
                                    '&.Mui-focused': {
                                        color: cores.primaria.principal
                                    }
                                }}
                            >
                                Eu sou
                            </FormLabel>
                            <RadioGroup
                                row
                                aria-label="cargo"
                                name="cargo"
                                value={cargo}
                                onChange={(e) => setCargo(e.target.value)}
                                sx={{
                                    justifyContent: 'center',
                                    '& .MuiFormControlLabel-root': {
                                        '& .MuiRadio-root.Mui-checked': {
                                            color: cores.primaria.principal
                                        }
                                    }
                                }}
                            >
                                <FormControlLabel value="ALUNO" control={<Radio />} label="Aluno(a)" />
                                <FormControlLabel value="PROFESSOR" control={<Radio />} label="Professor(a)" />
                            </RadioGroup>
                        </FormControl>

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="nome"
                            label="Nome Completo"
                            name="nome"
                            autoFocus
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            disabled={carregando}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: cores.fundo.claro,
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: cores.primaria.principal,
                                },
                            }}
                        />

                        {cargo === 'ALUNO' && (
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="matricula"
                                label="Matrícula"
                                name="matricula"
                                value={matricula}
                                onChange={(e) => setMatricula(e.target.value)}
                                disabled={carregando}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: cores.fundo.claro,
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: cores.primaria.principal,
                                    },
                                }}
                            />
                        )}

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Endereço de E-mail"
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={carregando}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: cores.fundo.claro,
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: cores.primaria.principal,
                                },
                            }}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="senha"
                            label="Senha"
                            type={mostrarSenha ? 'text' : 'password'}
                            id="senha"
                            value={senha}
                            onChange={(e) => setSenha(e.target.value)}
                            disabled={carregando}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: cores.fundo.claro,
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: cores.primaria.principal,
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="alternar visibilidade da senha"
                                            onClick={() => setMostrarSenha(!mostrarSenha)}
                                            edge="end"
                                            disabled={carregando}
                                            size="small"
                                        >
                                            {mostrarSenha ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmarSenha"
                            label="Confirmar Senha"
                            type={mostrarConfirmarSenha ? 'text' : 'password'}
                            id="confirmarSenha"
                            value={confirmarSenha}
                            onChange={(e) => setConfirmarSenha(e.target.value)}
                            disabled={carregando}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: cores.fundo.claro,
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: cores.primaria.principal,
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="alternar visibilidade da confirmação de senha"
                                            onClick={() => setMostrarConfirmarSenha(!mostrarConfirmarSenha)}
                                            edge="end"
                                            disabled={carregando}
                                            size="small"
                                        >
                                            {mostrarConfirmarSenha ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={carregando || !nome || !email || !senha || !confirmarSenha || (cargo === 'ALUNO' && !matricula)}
                            sx={{
                                mt: 3,
                                mb: 2,
                                height: 48,
                                borderRadius: 2,
                                background: cores.gradientes.primario,
                                fontWeight: 'bold',
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: cores.sombras.botoes,
                                '&:hover': {
                                    background: cores.gradientes.secundario,
                                },
                                '&:disabled': {
                                    background: '#e2e8f0',
                                    color: cores.texto.desabilitado,
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            {carregando ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Cadastrar'}
                        </Button>

                        <Typography variant="body2" align="center" sx={{ color: cores.texto.terciario, mt: 2 }}>
                            Já tem uma conta?{' '}
                            <RouterLink to="/login" style={{ color: cores.primaria.principal, fontWeight: 600, textDecoration: 'none' }}>
                                Entre
                            </RouterLink>
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default CadastroPagina;